import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "../css/ProfileAcademy.css";

const BACKEND_URL = "https://linxlegacyacademy.dk";

export default function ProfileAcademy() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState({});
  const [holdInfo, setHoldInfo] = useState(null);
  const navigate = useNavigate();

  const editableFields = ["email", "phone", "ingame_name"];
  const personalFields = {
    personal_id: "Personlige ID",
    discord_id: "Discord ID",
    username: "Brugernavn",
    email: "E-mail",
    phone: "Telefon",
    ingame_name: "Ingame Navn",
  };

  // -------------------------
  // Hent brugerdata
  // -------------------------
  useEffect(() => {
    async function fetchUser() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/profile/user`, {
          credentials: "include",
        });
        const data = await res.json();
        if (data.status === "logged_in") {
          setUser(data);
          setEditData({ ...data });
        } else {
          navigate("/login");
        }
      } catch (err) {
        console.error("Fejl ved hentning af bruger:", err);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    }

    async function fetchHoldInfo() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/profile/user/holdinfo`, {
          credentials: "include",
        });
        const data = await res.json();
        setHoldInfo(data); // kan håndtere status direkte i JSX
      } catch (err) {
        console.error("Fejl ved hentning af hold info:", err);
        setHoldInfo({ status: "error" });
      }
    }

    fetchUser();
    fetchHoldInfo();
  }, [navigate]);

  // -------------------------
  // Gem profil
  // -------------------------
  const saveProfile = async () => {
    if (!user) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/profile/user`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editData),
      });
      const updated = await res.json();
      if (updated.status === "success") {
        setUser({ ...user, ...updated });
        alert("Profil opdateret!");
      } else {
        alert("Kunne ikke opdatere profil: " + updated.status);
      }
    } catch (err) {
      console.error(err);
      alert("Kunne ikke opdatere profil.");
    }
  };

  // -------------------------
  // Stripe checkout
  // -------------------------
  const payPackage = async () => {
    if (!user?.stripe_price_id) return alert("Ingen pakke valgt");

    try {
      const res = await fetch(`${BACKEND_URL}/api/profile/user/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price_id: user.stripe_price_id }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert("Der skete en fejl ved Stripe checkout.");
    } catch (err) {
      console.error("Fejl ved Stripe betaling:", err);
      alert("Kunne ikke starte betaling.");
    }
  };

  // -------------------------
  // Loading
  // -------------------------
  if (loading) return <p className="loading-text">Indlæser...</p>;
  if (!user) return null;

return (
  <div className="profile-container">

    {/* Header */}
    <div className="profile-header">
      <h1 className="welcome-text">Velkommen - {user.username}</h1>
    </div>

    {/* Personlige oplysninger */}
    <div className="profile-card">
      <h2>Personlige oplysninger</h2>

      {Object.keys(personalFields).map((key) => (
        <div key={key} className="form-group">
          <label>{personalFields[key]}</label>

          <input
            type="text"
            value={editData[key] || ""}
            readOnly={!editableFields.includes(key)}
            onChange={(e) => {
              if (editableFields.includes(key)) {
                setEditData({ ...editData, [key]: e.target.value });
              }
            }}
            className={!editableFields.includes(key) ? "readonly-input" : ""}
          />
        </div>
      ))}

      <button className="btn btn-save" onClick={saveProfile}>
        Gem ændringer
      </button>
    </div>


    {/* Hold Info */}
    <div className="profile-card">

      <h2>Hold Info</h2>

      {holdInfo ? (
        holdInfo.status === "success" ? (
          <>
            <p>Holdnavn: {holdInfo.holdnavn}</p>

            <h3>Mine spil tider</h3>

            {holdInfo.game_times?.length > 0 ? (
              <ul>
                {holdInfo.game_times.map((gt, idx) => (
                  <li key={idx}>
                    {gt.game_name} - {gt.day_name} {gt.start_time} til {gt.end_time}
                  </li>
                ))}
              </ul>
            ) : (
              <p>Ingen spil tider registreret</p>
            )}
          </>
        ) : holdInfo.status === "no_team" ? (
          <p>Du er ikke tilknyttet et hold.</p>
        ) : (
          <p>Kunne ikke hente hold info fra serveren.</p>
        )
      ) : (
        <p>Indlæser hold info...</p>
      )}

    </div>


    {/* Pakke */}
    <div className="profile-card package-card">

      <div className="package-header">

        <h2>{user.stripe_product_name || "Support"}</h2>

        {user.payment_status && (
          (() => {

            const statusMap = {
              active: { label: "Betalt ✅", className: "paid" },
              paid: { label: "Betalt ✅", className: "paid" },
              past_due: { label: "Betaling fejlet ⚠️", className: "past_due" },
              canceled: { label: "Annulleret ❌", className: "canceled" },
              manual: { label: "Manuel behandling 🕒", className: "manual" },
              incomplete: { label: "Ufuldstændig betaling ⚠️", className: "incomplete" },
              trialing: { label: "Prøveperiode ⏳", className: "trialing" },
              unpaid: { label: "Ikke betalt ❌", className: "unpaid" },
            };

            const { label, className } = statusMap[user.payment_status] || {
              label: "Ikke betalt ❌",
              className: "unpaid",
            };

            return (
              <span className={`package-status ${className}`}>
                {label}
              </span>
            );

          })()
        )}

      </div>

      {user.stripe_price_id && user.payment_status !== "paid" && (
        <button className="btn-pay" onClick={payPackage}>
          Betal pakke
        </button>
      )}

      <ul className="package-features">
        <li>🎮 Adgang til holdtræning</li>
        <li>💬 Direkte support</li>
        <li>📅 Eksklusive events</li>
      </ul>

    </div>


    {/* Kalender nederst */}
    {holdInfo?.status === "success" && (
      <div className="profile-card calendar-card">

        <h2>Hold Kalender</h2>

        <div className="calendar-container">
          <FullCalendar
  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
  initialView={window.innerWidth < 768 ? "timeGridWeek" : "dayGridMonth"}
  headerToolbar={{
    left: "prev,next",
    center: "title",
    right: "dayGridMonth,timeGridWeek",
  }}
  height="auto"
  events={holdInfo.events.map((ev) => ({
    title: ev.titel,
    start: ev.start,
    end: ev.slut,
  }))}
/>
        </div>

      </div>
    )}

  </div>
);
}